import { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { ListChecks, SealCheck, Scales, ShieldCheck, Flag, ArrowRight, X, CheckCircle, Compass } from "@phosphor-icons/react";

const STORE = "lottie_guided";
const SKIP = "lottie_guided_skipped";

function buildSteps(d) {
  const overdue = d.journals_overdue?.count || 0;
  const due = d.journals_due?.count || 0;
  const post = d.journals_to_post != null ? d.journals_to_post : (overdue + due);
  const review = { key: "review", label: "Review imported items", icon: ListChecks, count: d.unscheduled_count || 0, to: "/unscheduled" };
  const postStep = { key: "post", label: "Post due journals", icon: SealCheck, count: post, urgent: overdue > 0, to: "/close" };
  const reconcile = { key: "reconcile", label: "Reconcile balances", icon: Scales, count: d.reconciliation_variances || 0, to: "/reconciliation" };
  const approve = { key: "approve", label: "Clear approvals", icon: ShieldCheck, count: d.approvals_waiting || 0, to: "/approvals" };
  const signoff = { key: "signoff", label: "Sign off the period", icon: Flag, count: 0, to: "/close", isSignoff: true };
  const withDone = [review, postStep, reconcile, approve].map((s) => ({ ...s, done: s.count === 0 }));
  const priorDone = withDone.every((s) => s.done);
  signoff.done = !!(d.journey_signed_off ?? d.month_signed_off);
  signoff.locked = !priorDone;
  return [...withDone, signoff];
}

export default function GuidedFlow() {
  const nav = useNavigate();
  const loc = useLocation();
  const [active, setActive] = useState(() => sessionStorage.getItem(STORE) === "1");
  const [d, setD] = useState(null);
  const [skipped, setSkipped] = useState(() => {
    try { return new Set(JSON.parse(sessionStorage.getItem(SKIP) || "[]")); } catch { return new Set(); }
  });

  const fetchData = useCallback(async () => {
    try { const { data } = await api.get("/dashboard/overview"); setD(data); return data; }
    catch { return null; }
  }, []);

  // Activate from the Dashboard "Start here" button: fetch, then jump to the first step.
  useEffect(() => {
    const onStart = async () => {
      sessionStorage.setItem(STORE, "1");
      sessionStorage.removeItem(SKIP);
      setSkipped(new Set());
      setActive(true);
      const data = await fetchData();
      if (data) {
        const first = buildSteps(data).find((s) => !s.done && !s.locked);
        if (first) nav(first.to);
      }
    };
    window.addEventListener("guided:start", onStart);
    return () => window.removeEventListener("guided:start", onStart);
  }, [fetchData, nav]);

  // Keep the flow live as the user completes work (route changes + badge refreshes).
  useEffect(() => {
    if (!active) return;
    fetchData();
    const on = () => fetchData();
    window.addEventListener("badges:refresh", on);
    return () => window.removeEventListener("badges:refresh", on);
  }, [active, loc.pathname, fetchData]);

  const exit = () => { sessionStorage.setItem(STORE, "0"); setActive(false); };
  const skip = (key) => {
    const nextSet = new Set(skipped); nextSet.add(key);
    setSkipped(nextSet);
    sessionStorage.setItem(SKIP, JSON.stringify([...nextSet]));
  };

  if (!active || !d || !d.has_tenant) return null;

  const steps = buildSteps(d);
  const doneCount = steps.filter((s) => s.done).length;
  const outstanding = steps.filter((s) => !s.done && !s.locked && !skipped.has(s.key));
  const current = outstanding[0] || null;
  const total = steps.length;

  return (
    <div data-testid="guided-flow"
      className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[70] w-[min(680px,calc(100vw-2rem))]">
      <div className="rounded-2xl bg-navy text-white shadow-2xl border border-white/10 px-4 py-3 flex items-center gap-3">
        <span className="w-9 h-9 rounded-xl bg-orange/20 flex items-center justify-center shrink-0">
          <Compass size={18} weight="fill" style={{ color: "#F37A24" }} />
        </span>
        {current ? (
          <>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="overline text-white/50">Guided month-end</span>
                <span className="text-[0.65rem] text-white/40 tabular">{doneCount}/{total} done</span>
              </div>
              <div className="flex items-center gap-2 mt-0.5">
                <current.icon size={15} weight="fill" style={{ color: current.urgent ? "#F87171" : "#F37A24" }} />
                <span className="font-heading font-bold truncate" data-testid="guided-current-label">{current.label}</span>
                {current.count > 0 && (
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full tabular ${current.urgent ? "bg-negative text-white" : "bg-orange text-white"}`} data-testid="guided-current-count">
                    {current.count}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              {loc.pathname !== current.to && (
                <button onClick={() => nav(current.to)} data-testid="guided-open"
                  className="inline-flex items-center gap-1.5 bg-orange text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-orangedark transition-colors">
                  Take me there <ArrowRight size={14} weight="bold" />
                </button>
              )}
              {loc.pathname === current.to && (
                <span className="text-xs text-white/60 hidden sm:inline pr-1">You're here — clear it, then continue</span>
              )}
              {!current.isSignoff && (
                <button onClick={() => skip(current.key)} data-testid="guided-skip"
                  className="text-white/60 hover:text-white text-sm font-medium px-3 py-2 rounded-lg hover:bg-white/10 transition-colors">
                  Skip
                </button>
              )}
              <button onClick={exit} data-testid="guided-exit" title="Exit guided mode"
                className="text-white/50 hover:text-white p-2 rounded-lg hover:bg-white/10 transition-colors"><X size={16} /></button>
            </div>
          </>
        ) : (
          <>
            <div className="min-w-0 flex-1 flex items-center gap-2">
              <CheckCircle size={18} weight="fill" className="text-positive shrink-0" />
              <span className="font-heading font-bold" data-testid="guided-clear">
                {steps.every((s) => s.done) ? "Month-end complete — nicely done." : "You've stepped through everything outstanding."}
              </span>
            </div>
            <button onClick={exit} data-testid="guided-finish"
              className="inline-flex items-center gap-1.5 bg-orange text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-orangedark transition-colors shrink-0">
              Finish
            </button>
          </>
        )}
      </div>
    </div>
  );
}
